import React, { memo, useCallback, Fragment, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import Form from 'Components/Form'
import { Input } from 'Components/Form/Input'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { SectionTitle } from 'Components/Section/SectionTitle'
import { SectionWrapper } from 'Components/Section/SectionWrapper'
import SubmitButton from 'Components/Form/SubmitButton'

import { number, required } from 'Utils/Validators/formValidation'
import {
  AMBASSADOR_PER_MISSION,
  CAMPAIGN_ENUM_TYPES,
  HOURS_PER_MISSION,
} from '../../../../shared/constants'
import { authorization } from '../../../../Module/Auth/Auth'
import { showReduxModal } from '../../../../Reducers/UI/actions'
import { deleteCityPlanById } from '../../../../Reducers/Campaigns/actions'
import { getCampaignLink } from '../../../../Utils/Link'
import { handleConfirmationDialog } from '../../../City/Page/Ambasadors/EmailPipelineView/utils'
import ConfirmDialog from '../../../../Components/ConfirmDialog/ConfirmDialog'

const EditCityPlanPaymentsAndCostsFormUnstyled = props => {
  const {
    className,
    onSubmit,
    campaign,
    payout,
    hardcodedBasePayout,
    hourlyPayout,
    distributionCost,
    clientMarginPercent,
    showReduxModal,
    deleteCityPlanById,
    cityPlan,
    city,
  } = props

  const isAdmin = authorization.isAdmin()
  const [missionType, setMissionType] = useState(
    !!hourlyPayout ? HOURS_PER_MISSION : AMBASSADOR_PER_MISSION,
  )
  const [hasError, setHasError] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState({ isOpen: false })

  const currentCity = useMemo(
    () =>
      campaign &&
      campaign.cityPlans &&
      campaign.cityPlans.nodes &&
      campaign.cityPlans.nodes.city,
    [campaign],
  )
  const { name: cityName } = currentCity || {}
  const { id: cityPlanId, missions } = cityPlan
  const { id: campaignId, type: campaignType } = campaign

  const handleFormUpdate = useCallback(
    (item, form) => {
      const { missionType } = form
      if (missionType && missionType.value) {
        setMissionType(missionType.value)
      }
    },
    [missionType],
  )

  const deleteCityPlan = async () => {
    const callbackPath = getCampaignLink(campaignId)
    deleteCityPlanById(cityPlanId, callbackPath)
  }

  const handleDeleteCityPlan = async () => {
    if (campaign.isSelfServe) {
      handleConfirmationDialog({
        title: 'Restricted!',
        contentText: `You cannot manually delete city plans for self-serve campaigns. If you need to do this, please contact tech.`,
        setConfirmDialogData,
        rightButtonLable: 'OK',
      })
    } else {
      const cityTitle = `${cityName ? cityName + ' ' : ''}City Plan`

      let statuses = missions.map(({ status }) => status)
      const draftMissions = statuses.filter(status => status === 'DRAFT').length

      if (statuses.length === 0) {
        // No missions
        showReduxModal({
          title: `Delete ${cityTitle}?`,
          children: `This city plan has 0 missions and can be deleted. Are you sure you want to proceed?`,
          onConfirm: deleteCityPlan,
          confirmLabel: 'OK',
        })
      } else if (draftMissions === statuses.length) {
        // All missions DRAFT
        showReduxModal({
          title: `Delete ${cityTitle}?`,
          children: `This city plan has ${draftMissions} DRAFT missions and can be deleted. The draft missions will be deleted as well. Are you sure you want to proceed?`,
          onConfirm: deleteCityPlan,
          confirmLabel: 'OK',
        })
      } else {
        // Has missions else than DRAFT
        showReduxModal({
          title: `Cannot delete ${cityTitle}`,
          children: `This city plan has missions in status other than Draft. You cannot delete this city plan.`,
          hideCancel: true,
          confirmLabel: 'OK',
        })
      }
    }
  }

  const handleFormSubmit = useCallback((form, isValid) => {
    // if (campaign.isSelfServe) {
    //   handleConfirmationDialog({
    //     title: 'Restricted!',
    //     contentText: `You cannot manually edit city plans for self-serve campaigns. If you need to do this, please contact tech.`,
    //     setConfirmDialogData,
    //     rightButtonLable: 'OK',
    //   })
    // } else
    if (isValid) {
      const prettyFormData = {}
      for (const [key, value] of Object.entries(form)) {
        const valueNumber = Number(value.value)
        prettyFormData[key] = valueNumber ? valueNumber : value.value
      }
      onSubmit(prettyFormData)
    }
  }, [])
  return (
    <Fragment>
      {confirmDialogData.isOpen ? (
        <ConfirmDialog {...confirmDialogData} />
      ) : null}
      <Form
        onUpdate={(item, form) => {
          handleFormUpdate(item, form)
          setHasError(
            Object.values(form).some(item => item.error && item.error !== ''),
          )
        }}
        onSubmit={handleFormSubmit}
      >
        <form className={className}>
          <SectionWrapper className="section-wrapper">
            <SectionTitle className="section-title">City Details</SectionTitle>
            <Grid container direction="row">
              <Grid display="flex">
                {CAMPAIGN_ENUM_TYPES[campaignType] === 'LETTERBOX' && (
                  <Grid>
                    <MissionTypeSelect initValue={missionType} />
                  </Grid>
                )}
                {missionType === HOURS_PER_MISSION ? (
                  <Fragment>
                    <Grid>
                      <HourlyPayoutInput initValue={hourlyPayout} />
                    </Grid>
                    <Grid>
                      <ClientMarginPercentInput
                        initValue={clientMarginPercent}
                      />
                    </Grid>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Grid>
                      <PayoutInput
                        initValue={payout}
                        hardcodedBasePayout={hardcodedBasePayout}
                        city={city}
                      />
                    </Grid>
                    <Grid>
                      <DistributionCostInput initValue={distributionCost} />
                    </Grid>
                  </Fragment>
                )}
              </Grid>
            </Grid>
          </SectionWrapper>
          <Grid container direction="row" justifyContent="flex-end" spacing={2}>
            <Grid item xs={'auto'}>
              <SubmitButton
                className="submit-button"
                value="Save"
                disabled={hasError}
              />
            </Grid>
            {isAdmin && cityPlanId && (
              <Grid item xs={'auto'}>
                <SubmitButton
                  value={'Delete City Plan'}
                  onClick={handleDeleteCityPlan}
                  cypress_id="edit-city-delete-city-plan-button"
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        </form>
      </Form>
    </Fragment>
  )
}

EditCityPlanPaymentsAndCostsFormUnstyled.propTypes = {
  cities: PropTypes.array.isRequired,
}

const mapStateToProps = (state, props) => {
  const { cityPlan, city } = props
  return {
    payout: cityPlan.payout,
    hardcodedBasePayout: cityPlan.hardcodedBasePayout,
    clientMarginPercent: cityPlan.clientMarginPercent,
    hourlyPayout: cityPlan.hourlyPayout,
    distributionCost: cityPlan.distributionCost,
    teamLeaderEnabled: city.teamLeaderEnabled,
    city: city,
  }
}

const MissionTypeSelect = memo(({ initValue }) => (
  <SelectDashboard
    label="Mission Type"
    name="missionType"
    initValue={initValue}
    options={[
      {
        label: 'Per Mission',
        value: AMBASSADOR_PER_MISSION,
      },
      // {
      //   label: 'Per Hour',
      //   value: HOURS_PER_MISSION,
      // },
    ]}
  />
))

const hardcodedPayoutValidation = (value, hardcodedBasePayout) => {
  return hardcodedBasePayout && value < hardcodedBasePayout
    ? 'Values below the contract specification are not permitted. For intentional adjustments, submit a request via the support channel.'
    : null
}

const PayoutInput = memo(({ initValue, hardcodedBasePayout, city }) => (
  <Input
    name="payout"
    label="BA Surge Or Letterbox Payout (Per Mission)"
    initValue={initValue}
    validator={[
      required,
      number,
      value => hardcodedPayoutValidation(value, hardcodedBasePayout),
    ]}
  />
))

const DistributionCostInput = memo(({ initValue }) => (
  <Input
    name="distributionCost"
    label="Distribution Cost For Customer (Per Mission)"
    initValue={initValue}
    validator={[required, number]}
  />
))

const HourlyPayoutInput = memo(({ initValue }) => (
  <Input
    name="hourlyPayout"
    label="BA hourly pay"
    placeholder="10"
    initValue={initValue}
    validator={[required, number]}
  />
))

const ClientMarginPercentInput = memo(({ initValue }) => (
  <Input
    name="clientMarginPercent"
    label="Client Margin (%)"
    placeholder="5"
    initValue={initValue}
    validator={[required, number]}
  />
))

export default connect(
  mapStateToProps,
  {
    showReduxModal,
    deleteCityPlanById,
  },
)(EditCityPlanPaymentsAndCostsFormUnstyled)
